import React, { useState, useEffect } from 'react';
import './style/App.css';
import {
  FaThermometerHalf,
  FaTint,
  FaCloud,
  FaInfoCircle,
  FaBurn
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

function Home() {
  const [userName, setUserName] = useState('');
  const [houses, setHouses] = useState([]);
  const [currentHouseIndex, setCurrentHouseIndex] = useState(0);
  const [rooms, setRooms] = useState([]);
  const [currentRoomIndex, setCurrentRoomIndex] = useState(0);
  const [roomSensors, setRoomSensors] = useState([]);
  const [countdown, setCountdown] = useState(10);
  const navigate = useNavigate();

  const currentHouse = houses[currentHouseIndex];
  const currentRoom = rooms[currentRoomIndex];
  const roomId = currentRoom?.room_id;

  // The five reading types that a "General" device should display
  const generalTypes = [
    'Temperature',
    'Humidity',
    'Gas Concentration',
    'Safety Valve',
  ];

  const displayTypeName = {
    'Gas Concentration': 'Gas in Air',
  };

  // Fetch user, houses, rooms… (same as before)
  useEffect(() => {
    fetch('/api/verify', { credentials: 'include' })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => setUserName(data.first_name))
      .catch(() => setUserName('Guest'));
  }, []);

  useEffect(() => {
    fetch('/api/houses', { credentials: 'include' })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => {
        setHouses(data.houses);
        setCurrentHouseIndex(0);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (!currentHouse) return setRooms([]);
    fetch(`/api/rooms?house_id=${currentHouse.house_id}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => {
        setRooms(data.rooms);
        setCurrentRoomIndex(0);
      })
      .catch(console.error);
  }, [currentHouse]);

  // Fetch latest readings
  useEffect(() => {
    if (!roomId) return setRoomSensors([]);
    let cd = 10;
    setCountdown(cd);

    const fetchSensors = () => {
      fetch(`/api/room-sensors?room_id=${roomId}`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : Promise.reject())
        .then(data => {
          // 1) find the gas concentration reading
          const gasConc = data.find(s => s.reading_type === 'Gas Concentration');
          // No alerts for ppm

          setRoomSensors(data);
        })
        .catch(err => {
          console.error('Failed to fetch room sensors:', err);
          setRoomSensors([]);
        });
    };

    fetchSensors();
    const interval = setInterval(() => {
      cd--;
      if (cd <= 0) {
        fetchSensors();
        cd = 10;
      }
      setCountdown(cd);
    }, 1000);

    return () => clearInterval(interval);
  }, [roomId]);

  const handleNextHouse = () => {
    if (!houses.length) return;
    setCurrentHouseIndex(i => (i + 1) % houses.length);
    setCountdown(10);
  };
  const handleNextRoom = () => {
    if (!rooms.length) return;
    setCurrentRoomIndex(i => (i + 1) % rooms.length);
    setCountdown(10);
  };
  const handleLogout = async () => {
    await fetch('/api/logout', {
      method: 'POST',
      credentials: 'include'
    });
    navigate('/');
  };

  // Build a list of cards to display:
  const cardsToDisplay = [];
  const hasGeneral = roomSensors.some(s => s.device_type === 'General');

  if (hasGeneral) {
    // For each of the 4 general types, pull its latest reading (or N/A)
    generalTypes.forEach(type => {
      const reading = roomSensors.find(s => s.device_type === 'General' && s.reading_type === type);
      cardsToDisplay.push({
        type,
        value: reading?.value ?? 'N/A'
      });
    });
  }

  return (
    <div className="app">
      <header className="header-bar">
        <img src="/Logo.png" alt="Logo" className="company-logo" />
        <h1 className="header-title">Welcome {userName}</h1>
        <button onClick={handleLogout} className="logout-btn">Logout</button>
        <img src="/Logo01.png" alt="Company Logo" className="company-logo-euro" />
      </header>

      <div className="grid">
        {cardsToDisplay.map((sensor, idx) => {
          const { type, value } = sensor;
          let icon = null;
          let cardClass = 'card';
          let normalRange = null;
          let valueFontSize = '24px';

          switch (type) {
            case 'Temperature':
              icon = <FaThermometerHalf className="icon" />;
              const temp = parseFloat(value);
              cardClass += temp < 18 ? ' red'
                : temp <= 25 ? ' green'
                : temp <= 28 ? ' yellow'
                : ' red';
              normalRange = '18-25°C';
              break;
            case 'Humidity':
              icon = <FaTint className="icon" />;
              const hum = parseFloat(value);
              cardClass += hum < 30 ? ' red'
                : hum <= 60 ? ' green'
                : hum <= 70 ? ' yellow'
                : ' red';
              normalRange = '40-60%';
              break;
            case 'Gas Concentration':
              icon = <FaCloud className="icon" />;
              const gas = parseFloat(value);
              cardClass += gas < 200 ? ' green'
                : gas < 1500 ? ' yellow'
                : ' red';
              normalRange = '<200 ppm';
              valueFontSize = '20px';
              break;
            case 'Safety Valve':
              break;
            default:
              break;
          }

          const content =
            type === 'Safety Valve' ? (
              <>
                <p className="valve-label">Safety Valve</p>
                <button className={`valve-btn ${value === 'OPEN' ? 'open' : 'closed'}`}>
                  {value}
                </button>
              </>
            ) : (
              <div className="icon-text">
                {icon}
                <div>
                  <p>{displayTypeName[type] ?? type}</p>
                  <h2 style={{ fontSize: valueFontSize }}>
                    {value}
                    {type === 'Temperature' ? '°C' : ''}
                    {type === 'Gas Level' ? '%' : ''}
                    {type === 'Humidity' ? '%' : ''}
                    {type === 'Gas Concentration' ? ' ppm' : ''}
                  </h2>
                  {normalRange && <p style={{ fontSize: '0.7em', marginTop: 2 }}>Normal: {normalRange}</p>}
                </div>
              </div>
            );

          return (
            <div key={idx} className={cardClass} style={type === 'Safety Valve' ? { opacity: 0.5 } : {}}>
              {content}
            </div>
          );
        })}

        {/* Room selector */}
        <div className="card span-2 room-card">
          <p className="valve-label">{currentRoom?.room_name || 'No room created'}</p>
          <button className="valve-btn" onClick={handleNextRoom}>
            {rooms.length === 0
              ? `(${currentRoomIndex}/${rooms.length})`
              : `Next Room (${currentRoomIndex + 1}/${rooms.length})`
            }
          </button>
        </div>

        {/* Countdown */}
        <div className="card countdown-card">
          <p>Next Update In</p>
          <h2>{countdown}s</h2>
        </div>

        {/* House Gas Level */}
        {currentHouse && (() => {
          const { gas_type: type, gas_level: level } = currentHouse;

          const colorClass = (() => {
            const g = parseFloat(level ?? 0);
              if (g >= 80 ) return 'green';
              if (g <= 30) return 'red';
              if (g <= 79 || g >= 31) return 'orange';
              return 'white';
          })();

          if (level == null) {
            return (
              <div className="card gas-level-card white">
                <div className="icon-text">
                  <div>
                    <p style={{ fontSize: '0.9em', marginTop: 6, color: '#e74c3c' }}>
                      Connect a gas level sensor to see the value
                    </p>
                    <p style={{ fontSize: '0.8em', marginTop: 4 }}>
                      Type: Propane
                    </p>
                  </div>
                </div>
              </div>
            );
          }

          // Else — natural (100%) or propane with reading
          return (
            <div className={`card gas-level-card ${colorClass}`}>
              <div className="icon-text">
                <FaBurn className="icon" />
                <div>
                  <p>Gas Level</p>
                  <h2>
                    {level != null ? `${level}%` : 'N/A'}
                  </h2>
                  <p style={{ fontSize: '0.8em', marginTop: 4 }}>
                    Type: {type ?? 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          );
        })()} 

        {/* Nouvelle légende */}
        <div className="card legend-card" style={{ gridColumn: 3, gridRow: 1 }}>
          <div className="icon-text">
            <FaInfoCircle className="icon" />
            <div>
              <p>Information</p>
              <ul style={{ listStyle: 'none', padding: 0, fontSize: '0.8em' }}>
                <li><span style={{ display: 'inline-block', width: '10px', height: '10px', backgroundColor: '#2ecc71', marginRight: '5px' }}></span>Normal</li>
                <li><span style={{ display: 'inline-block', width: '10px', height: '10px', backgroundColor: '#ffcc00', marginRight: '5px' }}></span>Warning</li>
                <li><span style={{ display: 'inline-block', width: '10px', height: '10px', backgroundColor: '#e74c3c', marginRight: '5px' }}></span>Danger</li>
              </ul>
            </div>
          </div>
        </div>

        {/* House selector */}
        <div className="card span-2 house-card">
          <p className="valve-label">{currentHouse?.street || 'No house'}</p>
          <button className="valve-btn" onClick={handleNextHouse}>
            Next House ({currentHouseIndex + 1}/{houses.length})
          </button>
        </div>
      </div>
    </div>
  );
}

export default Home;